import { saveAs } from 'file-saver';
import { marked } from 'marked';
import {
    Document,
    Paragraph,
    HeadingLevel,
    TextRun,
    Packer,
    Table,
    TableRow,
    TableCell,
    WidthType,
} from 'docx';

import { signalBand } from './RisksSection';

// Shared PDF / Word export for a single chat answer. Used by the single-report
// Ask Prompta chat and the compare-report chat so both screens produce the same
// document from the same markdown.

// A risk answer carries a risk_meta frame whose per-pillar scores are rendered
// on screen as score cards. Flatten it into groups the exporters can print, so
// the cards survive into the document instead of being dropped with the rest of
// the visuals. Two shapes arrive: a comparison sends `reports[]` (one entry per
// compared report), a single report sends bare `scores`.
export const riskScoreGroups = (riskMeta) => {
    if (!riskMeta) return [];

    const perReport = (riskMeta.reports || []).filter(
        (r) => r && r.scores && Object.keys(r.scores).length > 0
    );

    if (perReport.length > 0) {
        return perReport.map((r, i) => ({
            label: r.label || (r.wave != null ? `Wave ${r.wave}` : `Report ${r.report_id ?? i + 1}`),
            scores: r.scores,
        }));
    }

    const scores = riskMeta.scores;
    if (!scores || Object.keys(scores).length === 0) return [];
    return [{ label: null, scores }];
};

const pillarName = (pillar) =>
    pillar.charAt(0).toUpperCase() + pillar.slice(1).toLowerCase();

// Scores arrive on the SIGNAL scale ([-1, 1], higher is better) — banded with
// signalBand, matching RiskScoreCards.
const scoreEntries = (scores) =>
    Object.entries(scores).map(([pillar, val]) => ({
        name: pillarName(pillar),
        value: Number(val).toFixed(3),
        band: signalBand(val),
    }));

const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

// Score cards as inline-styled HTML — html2pdf rasterizes the container, so the
// band colours carry over to the PDF exactly as they look on screen.
const riskScoresHtml = (riskMeta) => {
    const groups = riskScoreGroups(riskMeta);
    if (groups.length === 0) return '';

    return groups
        .map((group) => {
            const cards = scoreEntries(group.scores)
                .map(
                    ({ name, value, band }) => `
                    <td class="score-card" style="background:${band.bg}; border-left:3px solid ${band.color};">
                        <div class="score-name">${escapeHtml(name)}</div>
                        <div class="score-value" style="color:${band.color};">${value}</div>
                        <div class="score-band">${escapeHtml(band.label)}</div>
                    </td>`
                )
                .join('');
            const heading = group.label
                ? `<div class="score-group-title">${escapeHtml(group.label)}</div>`
                : '';
            return `${heading}<table class="score-cards"><tr>${cards}</tr></table>`;
        })
        .join('');
};

export const exportAnswerAsPdf = async (content, filename, { riskMeta } = {}) => {
    const html2pdf = (await import('html2pdf.js')).default;
    const container = document.createElement('div');
    container.innerHTML = `
        <style>
            body { font-family: Arial, sans-serif; font-size: 13px; color: #222; line-height: 1.6; }
            h1 { font-size: 20px; } h2 { font-size: 17px; } h3 { font-size: 15px; }
            pre { background: #f5f5f5; padding: 8px; border-radius: 4px; }
            code { background: #f0f0f0; padding: 2px 4px; border-radius: 3px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ccc; padding: 6px 10px; }
            th { background: #f0f0f0; }
            .score-group-title { font-weight: 700; color: #0d47a1; margin: 10px 0 4px; }
            table.score-cards { table-layout: fixed; margin-bottom: 10px; }
            table.score-cards td { border: none; border-radius: 0 6px 6px 0; padding: 6px 10px; vertical-align: top; }
            .score-name { font-size: 10px; color: #666; }
            .score-value { font-size: 15px; font-weight: 600; }
            .score-band { font-size: 9px; color: #888; }
        </style>
        ${riskScoresHtml(riskMeta)}
        ${marked(content)}
    `;
    document.body.appendChild(container);
    try {
        await html2pdf().set({
            margin: [15, 15],
            filename,
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        }).from(container).save();
    } finally {
        document.body.removeChild(container);
    }
};

// Word has no card layout, so the same scores print as one bold-labelled line
// per pillar under an optional per-report heading.
const riskScoreParagraphs = (riskMeta) => {
    const groups = riskScoreGroups(riskMeta);
    if (groups.length === 0) return [];

    const paragraphs = [];
    groups.forEach((group) => {
        if (group.label) {
            paragraphs.push(
                new Paragraph({ text: group.label, heading: HeadingLevel.HEADING_3 })
            );
        }
        scoreEntries(group.scores).forEach(({ name, value, band }) => {
            paragraphs.push(
                new Paragraph({
                    children: [
                        new TextRun({ text: `${name}: `, bold: true }),
                        new TextRun({ text: `${value} (${band.label})` }),
                    ],
                })
            );
        });
        paragraphs.push(new Paragraph({ text: '' }));
    });
    return paragraphs;
};

// Inline tokens (bold / italic / code / plain) as docx runs. Shared by
// paragraphs and table cells so a **bold** cell reads the same in both.
// `bold` force-bolds every run, for header cells. It is spread rather than set
// outright so a body run carries no bold property at all, instead of an
// explicit bold=false.
const inlineRuns = (tokens, fallbackText, { bold = false } = {}) => {
    const b = bold ? { bold: true } : {};
    const runs = [];
    for (const inline of (tokens || [])) {
        if (inline.type === 'strong') {
            runs.push(new TextRun({ text: inline.text, bold: true }));
        } else if (inline.type === 'em') {
            runs.push(new TextRun({ text: inline.text, italics: true, ...b }));
        } else if (inline.type === 'codespan') {
            runs.push(new TextRun({ text: inline.text, font: 'Courier New', size: 20, ...b }));
        } else {
            runs.push(new TextRun({ text: inline.raw || inline.text || '', ...b }));
        }
    }
    return runs.length ? runs : [new TextRun({ text: fallbackText || '', ...b })];
};

// The change plan arrives with real markdown tables — the adoption-plan table
// and the "Where the register has gaps" table. marked lexes those into a single
// `table` token, so without this branch every row is dropped from the .docx.
const tableCell = (cell, { header = false } = {}) => new TableCell({
    children: [new Paragraph({ children: inlineRuns(cell.tokens, cell.text, { bold: header }) })],
});

const markdownTable = (token) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
        new TableRow({
            tableHeader: true,
            children: (token.header || []).map((cell) => tableCell(cell, { header: true })),
        }),
        ...(token.rows || []).map(
            (row) => new TableRow({ children: row.map((cell) => tableCell(cell)) })
        ),
    ],
});

export const markdownToParagraphs = (content) => {
    const paragraphs = [];
    for (const token of marked.lexer(content)) {
        if (token.type === 'heading') {
            const levelMap = {
                1: HeadingLevel.HEADING_1,
                2: HeadingLevel.HEADING_2,
                3: HeadingLevel.HEADING_3,
                4: HeadingLevel.HEADING_4,
                5: HeadingLevel.HEADING_5,
                6: HeadingLevel.HEADING_6,
            };
            paragraphs.push(new Paragraph({ text: token.text, heading: levelMap[token.depth] || HeadingLevel.HEADING_1 }));
        } else if (token.type === 'paragraph') {
            paragraphs.push(new Paragraph({ children: inlineRuns(token.tokens, token.text) }));
        } else if (token.type === 'table') {
            paragraphs.push(markdownTable(token));
            // Word merges tables that touch, so keep a spacer between a table
            // and whatever follows it.
            paragraphs.push(new Paragraph({ text: '' }));
        } else if (token.type === 'code') {
            for (const line of token.text.split('\n')) {
                paragraphs.push(new Paragraph({ children: [new TextRun({ text: line, font: 'Courier New', size: 20 })] }));
            }
        } else if (token.type === 'list') {
            for (const item of token.items) {
                paragraphs.push(new Paragraph({ text: item.text, bullet: { level: 0 } }));
            }
        } else if (token.type === 'space') {
            paragraphs.push(new Paragraph({ text: '' }));
        }
    }
    return paragraphs;
};

export const exportAnswerAsWord = async (content, filename, { riskMeta } = {}) => {
    const children = [...riskScoreParagraphs(riskMeta), ...markdownToParagraphs(content)];
    const doc = new Document({ sections: [{ children }] });
    saveAs(await Packer.toBlob(doc), filename);
};

export const exportTimestamp = () => new Date().toISOString().slice(0, 10);
